<template>
  <vd-error
    v-if="!queryLoading && notFound"
    status="404"
    message="The segment you are looking for does not exist"
    :back-link="segmentsListPageLink"
  />
  <vd-error
    v-else-if="!queryLoading && segmentId && !inWIPVersion && !preview"
    status="405"
    message="You can not edit the old version of a segment"
    :back-link="segmentsListPageLink"
  />
  <vd-error
    v-else-if="!queryLoading && !segmentId && !inWIPVersion"
    status="405"
    message="You can not link a segment in an old version"
    :back-link="segmentsListPageLink"
  />
  <div v-else class="segment-form-page">
    <vd-page-form
      class="segment-form"
      :preview="preview"
      :title="pageTitle"
      :back-link="segmentsListPageLink"
      :edit-link="segmentsEditPageLink"
      :form="form"
      :hide-actions="!inWIPVersion || isSystem || !canWriteSegments"
      :save-callback="saveForm"
      :save-loading="saveLoading"
      :loading="queryLoading"
      :delete-loading="deleteSegmentLoading"
      :allow-delete="!!segmentId || !canWriteSegments"
      @delete="() => onSegmentDelete(segmentId)"
    >
      <template #title-extension>
        <div class="title-extension">
          <el-tag v-if="isSystem" size="small" type="info">system</el-tag>
        </div>
      </template>

      <VdFormField
        v-model="form.name"
        prop="name"
        label="Name"
        type="text"
        required
        max-length="40"
        show-word-limit
        update-on-blur-only
      />

      <VdFormField
        v-model="form.description"
        prop="description"
        label="Description"
        type="textarea"
        max-length="150"
        show-word-limit
        :auto-size="{ minRows: 3, maxRows: 4 }"
        update-on-blur-only
      />

      <VdFormField prop="conditions" :label="conditionsLabel">
        <SegmentConditionsForm
          :key="`${queryLoading}`"
          :model-value="form.conditions"
          :operator="SegmentConditionType.And"
          :features="features"
          :read-only="preview"
          :error="conditionErrors || {}"
          @update:model-value="onConditionUpdate"
        />
      </VdFormField>

      <div v-if="isSystem" class="system-explanation">A system segment can not be edited or deleted.</div>
    </vd-page-form>

    <!-- warning delete dialog -->
    <vd-dialog
      :visible="displayDeleteWarning"
      title="This segment can't be deleted"
      type="error"
      @close="closeDeleteWarning"
    >
      <span>You can't delete this segment as it is used</span>
      <strong v-if="deleteWarningDetails.configurationRules.length">
        in {{ deleteWarningDetails.configurationRules.length }}
        {{ pluralize("configuration rule", deleteWarningDetails.configurationRules.length) }}
      </strong>
      <span v-if="deleteWarningDetails.abtests.length && deleteWarningDetails.configurationRules.length">
        and&nbsp;
      </span>
      <span v-if="deleteWarningDetails.abtests.length">
        <strong>in the following A/B Tests:</strong>
        <ul class="delete-warning-list">
          <li v-for="abtest in deleteWarningDetails.abtests" :key="abtest.id">
            {{ abtest.name }}
          </li>
        </ul>
      </span>
      <template #footer>
        <vd-button type="secondary" skin="blue" @click="closeDeleteWarning">I understand</vd-button>
      </template>
    </vd-dialog>
  </div>
</template>

<script lang="ts">
/**
 * This page displays a segment form.
 */
import { VdFormField } from "@voodoo.io/voodoo-ui";
import { computed, defineComponent, reactive, ref, watch } from "vue";
import { useRouter } from "vue-router";

import { pluralize } from "@/filters";
import {
  DeleteSegmentMutationResponse,
  queryNames,
  SegmentConditionInput,
  SegmentConditionType,
  SegmentInput,
  useSegmentFormPageQuery,
  useSegmentFormPageSaveMutation,
  useSegmentsListPageDeleteMutation,
} from "@/graphql";
import { useMutationResponse } from "@/graphql/utils";
import usePermissions from "@/hooks/use-permissions";
import links from "@/router/links";
import { generateRandomId } from "@/utils";

import SegmentConditionsForm from "./components/SegmentConditionsForm.vue";
import { Condition, ConditionError, fillRowIds, getConditionsInError, sanitizeConditions } from "./utils";

const toConditionInput = (condition: Condition): SegmentConditionInput => ({
  type: condition.type,
  featureId: condition.featureId,
  operator: condition.operator,
  testValue: condition.testValue,
  conditions: (condition.conditions || []).map(toConditionInput),
});

interface Form extends Omit<SegmentInput, "conditions"> {
  conditions: Condition[];
}

const buildConditionErrors = (conditions: Condition[]) => {
  const errors = getConditionsInError(conditions);
  if (!errors.length) {
    return null;
  }
  return errors.reduce((acc, error) => ({ ...acc, [error.rowId]: error }), {});
};

export default defineComponent({
  name: "SegmentFormPage",

  components: {
    SegmentConditionsForm,
    VdFormField,
  },

  props: {
    gameId: {
      type: String,
      required: true,
    },

    versionId: {
      type: String,
      required: true,
    },

    /**
     * If the segmentId is present, update the segment, create it otherwise.
     */
    segmentId: {
      type: String,
      default: null,
    },

    /**
     * If true, enter in the preview mode
     */
    preview: {
      type: Boolean,
      default: false,
    },

    /**
     * If true, we are not editing the segment but create a new from segmentId
     */
    duplicate: {
      type: Boolean,
      default: false,
    },
  },

  setup(props) {
    const router = useRouter();
    const { canWriteSegments } = usePermissions();

    /**
     * Define the form
     */
    const form = reactive<Form>({
      name: "",
      description: "",
      conditions: [
        {
          type: SegmentConditionType.And,
          rowId: generateRandomId(),
          conditions: [
            {
              type: SegmentConditionType.Test,
              rowId: generateRandomId(),
              featureId: null,
              operator: null,
              testValue: null,
            },
          ],
        },
      ],
    });

    /**
     * Handle condition errors
     */
    const conditionErrors = ref<Record<string, ConditionError> | null>(null);

    /**
     * Try to fetch the segment from its id.
     * If we have a segment, update the form title and fill the form
     */
    const { result, loading: queryLoading } = useSegmentFormPageQuery(
      {
        gameId: props.gameId,
        versionId: props.versionId,
        segmentId: props.segmentId,
      },
      { fetchPolicy: "network-only" }
    );

    const inWIPVersion = computed(() => result.value?.game?.version?.isWip ?? false);

    const segment = computed(() => result.value?.game?.version?.segment);
    const isSystem = computed(() => result.value?.game?.version?.segment?.isSystem ?? false);

    const notFound = computed(() => !queryLoading.value && props.segmentId && !segment.value);

    const features = computed(() => result.value?.game?.version?.features ?? []);

    const conditionsLabel = computed(
      () => (form.conditions.length && "Include users that meet the following conditions:") || ""
    );

    const pageTitle = computed(() => {
      if (props.segmentId) {
        const title = (segment.value && segment.value.name) || "";
        if (props.duplicate) {
          return `Duplicate of ${title}`;
        }
        return title;
      }
      return "New segment";
    });

    watch(segment, (value) => {
      if (value) {
        form.name = props.duplicate ? `Duplicate of ${value.name}` : value.name;
        form.description = value.description;
        form.conditions = (value.conditions || []).length
          ? fillRowIds(value.conditions)
          : [
              {
                type: SegmentConditionType.And,
                rowId: generateRandomId(),
                conditions: [
                  {
                    type: SegmentConditionType.Test,
                    rowId: generateRandomId(),
                    featureId: null,
                    operator: null,
                    testValue: null,
                  },
                ],
              },
            ];
      }
    });

    const onConditionUpdate = (conditions: Condition[]) => {
      form.conditions = sanitizeConditions(conditions);
    };

    /**
     * Define the location to return after save/deletion/cancel
     */
    const segmentsListPageLink = {
      name: links.games.segments.list,
      params: { gameId: props.gameId, versionId: props.versionId },
    };

    const segmentsEditPageLink = {
      name: links.games.segments.edit,
      params: { gameId: props.gameId, versionId: props.versionId, segmentId: props.segmentId },
    };

    /**
     * Save the segment
     */
    const { mutate: save, loading: saveLoading } = useSegmentFormPageSaveMutation({});

    /**
     * Check every 500ms that the conditions has no errors.
     * If so, the timeout is cleared.
     */
    const checkConditionsTimeout = ref(-1);
    const checkConditions = () => {
      const errors = buildConditionErrors(form.conditions);
      if (!errors) {
        window.clearTimeout(checkConditionsTimeout.value);
        checkConditionsTimeout.value = -1;
        conditionErrors.value = {};
        return null;
      }
      checkConditionsTimeout.value = window.setTimeout(() => {
        checkConditions();
      }, 500);
      return errors;
    };

    /**
     * On save, check that every conditions are filled.
     * If not, abort saving and fill conditionErrors.
     * Otherwise: perform the save and redirect to the segments list page
     */
    const saveForm = async () => {
      conditionErrors.value = checkConditions();

      if (conditionErrors.value) {
        window.scrollTo(0, 0);
        return;
      }

      const response = await save({
        gameId: props.gameId,
        versionId: props.versionId,
        input: {
          name: form.name,
          description: form.description,
          conditions: sanitizeConditions(form.conditions).map(toConditionInput),
        },
        segmentId: props.duplicate ? null : props.segmentId,
      });

      const data = useMutationResponse(response, (resp) => resp?.saveSegment);

      if (data?.success) {
        router.push(segmentsListPageLink);
      }
    };

    /**
     *  Delete a segment
     */
    const { mutate: deleteSegment, loading: deleteSegmentLoading } = useSegmentsListPageDeleteMutation({
      refetchQueries: [queryNames.SegmentsListPage],
    });

    /**
     * If the segment is used in an A/B Test cohort or a configuration rule,
     * we cannot delete it without corrupting the data integrity.
     * We therefore display an information dialog that explain to the user
     * that he has to remove the segment from those resource before continuing.
     */
    const deleteWarningDetails = reactive<{
      abtests: NonNullable<DeleteSegmentMutationResponse["usedInAbtests"]>;
      configurationRules: NonNullable<DeleteSegmentMutationResponse["usedInConfigurationRules"]>;
    }>({
      abtests: [],
      configurationRules: [],
    });

    const displayDeleteWarning = ref(false);

    const closeDeleteWarning = () => {
      displayDeleteWarning.value = false;
    };

    const onSegmentDelete = async (segmentId?: string) => {
      if (!segmentId) {
        return;
      }

      const response = await deleteSegment({ gameId: props.gameId, versionId: props.versionId, segmentId });
      const deleteResult = useMutationResponse(response, (resp) => resp?.deleteSegment);
      if (deleteResult?.isCurrentlyUsed) {
        deleteWarningDetails.abtests = deleteResult?.usedInAbtests ?? [];
        deleteWarningDetails.configurationRules = deleteResult?.usedInConfigurationRules ?? [];
        displayDeleteWarning.value = true;
      } else {
        router.push(segmentsListPageLink);
      }
    };

    return {
      pageTitle,
      queryLoading,
      segmentsListPageLink,
      segmentsEditPageLink,
      form,
      conditionsLabel,
      isSystem,
      onConditionUpdate,
      SegmentConditionType,
      conditionErrors,
      saveLoading,
      saveForm,
      features,
      notFound,
      inWIPVersion,
      onSegmentDelete,
      displayDeleteWarning,
      deleteWarningDetails,
      closeDeleteWarning,
      deleteSegmentLoading,
      canWriteSegments,
      pluralize,
    };
  },
});
</script>

<style lang="scss">
.segment-form-page {
  .vd-page-component .head .page-title {
    align-items: flex-end;

    .title-extension {
      margin-left: size("S");
      font-weight: 400;
    }
  }
  .system-explanation {
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }
}
</style>
